import { PropsWithChildren } from 'react';
import PageTitle from '@johnlewispartnership/wtr-ingredients/dist/ingredients/PageTitle';
import { Container } from '@johnlewispartnership/wtr-ingredients/dist/foundations/grid';
import { getAuthenticationProvider } from '@johnlewispartnership/wtr-website-authentication/dist/context';
import { AuthorizationServiceClient } from '@johnlewispartnership/wtr-website-authentication/dist/service';
import config from 'config';
import tokenServiceClient from 'services/token';

import Breadcrumbs from 'renderer/components/Breadcrumbs';
import type { ContentPageData } from 'services/content/types';
import CookieConsentModal from 'renderer/components/CookieConsent/CookieConsentModal';
import { CommonModalProvider } from 'renderer/components/CommonModal/context';
import CommonModal from 'renderer/components/CommonModal';
import { CookieConsentProvider } from 'renderer/components/CookieConsent/context';
import { PreloadedDataProvider } from 'renderer/context/preloadedDataContext';
import { PreloadedData } from 'server/types';

const authorizationServiceClient = new AuthorizationServiceClient(
  config.services.authorizationServer.url,
);

const AuthenticationProvider = getAuthenticationProvider({
  tokenServiceClient,
  tokenServiceBasePath: config.services.tokenClient.path,
  authorizationServiceClient,
});

type PageLayoutProps = PropsWithChildren<{
  pageData: ContentPageData;
  preloadedData?: PreloadedData;
}>;

const PageLayout = ({ children, pageData, preloadedData }: PageLayoutProps) => {
  const { authorableBreadcrumb, breadcrumbs, pageTitle, showPageTitle } = pageData;
  const { display, text } = pageTitle || {};

  const accessToken = preloadedData?.token?.accessToken;

  const defaultCookieConsent = {
    functionalCookiesAccepted: false,
    analyticsCookiesAccepted: false,
    advertisingCookiesAccepted: false,
    hasConsentCookie: false,
  };

  // The inline style is a hacky override until this is complete: WPIP-74692
  return (
    <CookieConsentProvider cookieConsent={defaultCookieConsent}>
      <AuthenticationProvider
        initialState={{
          initialised: !!accessToken,
          isLoggedIn: !!accessToken,
          hasTokenSession: !!accessToken,
          token: preloadedData?.token,
        }}
      >
        <CommonModalProvider>
          <PreloadedDataProvider preloadedData={preloadedData}>
            <Container data-testid="container" style={{ maxWidth: '1260px', padding: 0 }}>
              <Breadcrumbs authorableBreadcrumb={authorableBreadcrumb} breadcrumbs={breadcrumbs} />
              {showPageTitle && display && <PageTitle title={text} theme="underline" />}
            </Container>
            {children}
          </PreloadedDataProvider>
          <CookieConsentModal />
          <CommonModal />
        </CommonModalProvider>
      </AuthenticationProvider>
    </CookieConsentProvider>
  );
};

export default PageLayout;
